import { Icon } from '@src/components/Icon';
import { IconType } from '@src/IconType';
import { LoadingSpinner } from '@src/components/LoadingSpinner';
import { ValidationError } from '@src/components/ValidationError';
import { classNames } from '@src/utils/classNames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComboboxMultipleProperties } from '../ComboboxMultiple';

export type ComboboxAddOptionProperties<T> = Pick<
  ComboboxMultipleProperties<T>,
  | 'disabled'
  | 'addOption'
  | 'addOptionLabel'
  | 'inputFieldErrorTranslationKey'
  | 'buttonTestId'
> & {
  readonly isValid: boolean;
  readonly errorMessage: string;
  readonly filteredOptionsLength: number;
  readonly inputValue: string;
  readonly onOptionAdded: (option: T) => void;
  readonly setFailedToAddOption: (value: boolean) => void;
  readonly onOptionAddFinished: () => void;
};

export function ComboboxAddOption<T>({
  disabled = false,
  addOption,
  isValid,
  addOptionLabel,
  inputFieldErrorTranslationKey,
  errorMessage,
  filteredOptionsLength,
  inputValue,
  onOptionAdded,
  setFailedToAddOption,
  onOptionAddFinished,
  buttonTestId,
}: ComboboxAddOptionProperties<T>) {
  const [addingOption, setAddingOption] = useState(false);
  const { t } = useTranslation('errorMessages');

  function onAddOption() {
    return async () => {
      if (inputValue.length === 0 || !addOption) {
        return;
      }
      //   if (allOptions.some((option) => displayValue(option) === query)) {
      //     return;
      //   }
      setAddingOption(true);
      setFailedToAddOption(false);
      try {
        const newOption = await addOption(inputValue);
        onOptionAdded(newOption);
      } catch {
        setFailedToAddOption(true);
      } finally {
        setAddingOption(false);
        onOptionAddFinished();
      }
    };
  }

  if (!addOption) {
    return null;
  }

  return (
    <button
      className="text-blue-500 w-full h-full hover:bg-gray-100 rounded-xl disabled:cursor-not-allowed disabled:text-slate-500 text-left"
      disabled={addingOption || disabled || !isValid}
      data-testid={buttonTestId ?? 'add-option'}
      onClick={onAddOption()}
    >
      <div
        className={classNames(
          'w-full h-[1px] px-3 bg-slate-300',
          filteredOptionsLength === 0 && 'hidden',
        )}
      />
      <div className="mt-2 flex flex-row py-4 px-4 gap-2.5">
        {addingOption ? (
          <LoadingSpinner className="!h-6 !w-6 !m-0" />
        ) : (
          <Icon type={IconType.Plus} className="h-6 w-6" />
        )}
        {addOptionLabel}: {inputValue}
      </div>
      {!isValid && (
        <ValidationError
          isValid={isValid}
          errorMessage={
            inputFieldErrorTranslationKey
              ? `${errorMessage}: ${t(inputFieldErrorTranslationKey)}`
              : errorMessage
          }
        />
      )}
    </button>
  );
}
